import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { borrowerAPI } from '../../services/api';
import { toast } from 'sonner';
import { Save, DollarSign } from 'lucide-react';

const BorrowerDealCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    deal_nickname: '',
    property_city: '',
    property_state: '',
    property_zip: '',
    property_full_address: '',
    purchase_price: '',
    rehab_budget: '',
    arv: '',
    exit_strategy: '',
    lender_name: '',
    est_closing_date: '',
    reserve_required_amount: '',
    notes: '',
  });

  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dealData = {
        ...formData,
        purchase_price: parseFloat(formData.purchase_price),
        rehab_budget: parseFloat(formData.rehab_budget),
        arv: parseFloat(formData.arv),
        reserve_required_amount: parseFloat(formData.reserve_required_amount),
        est_closing_date: new Date(formData.est_closing_date).toISOString(),
      };

      const response = await borrowerAPI.createDeal(dealData);
      toast.success('Deal created successfully!');
      navigate(`/borrower/deals/${response.data.id}`);
    } catch (error) {
      console.error('Error creating deal:', error);
      toast.error(error.response?.data?.detail || 'Failed to create deal');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Layout title="Create New Deal">
      <Card className="glass-effect border-slate-200 dark:border-slate-700 animate-slide-in-up" data-testid="deal-create-form">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="w-10 h-10 gradient-primary rounded-lg flex items-center justify-center">
              <Save className="w-5 h-5 text-white" />
            </div>
            Deal Intake Form
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <div className="w-1 h-6 gradient-primary rounded-full"></div>
                Basic Information
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="deal_nickname">Deal Nickname *</Label>
                  <Input
                    id="deal_nickname"
                    name="deal_nickname"
                    value={formData.deal_nickname}
                    onChange={handleChange}
                    placeholder="My Investment Property"
                    required
                    data-testid="deal-nickname-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lender_name">Lender Name *</Label>
                  <Input
                    id="lender_name"
                    name="lender_name"
                    value={formData.lender_name}
                    onChange={handleChange}
                    placeholder="ABC Lending Corp"
                    required
                    data-testid="lender-name-input"
                  />
                </div>
              </div>
            </div>

            {/* Property Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <div className="w-1 h-6 gradient-secondary rounded-full"></div>
                Property Information
              </h3>
              
              <div className="space-y-2">
                <Label htmlFor="property_full_address">Full Property Address *</Label>
                <Input
                  id="property_full_address"
                  name="property_full_address"
                  value={formData.property_full_address}
                  onChange={handleChange}
                  placeholder="123 Main Street"
                  required
                  data-testid="property-address-input"
                />
                <p className="text-sm text-slate-500 dark:text-slate-400">This will only be visible to admin</p>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="property_city">City *</Label>
                  <Input
                    id="property_city"
                    name="property_city"
                    value={formData.property_city}
                    onChange={handleChange}
                    placeholder="Detroit"
                    required
                    data-testid="property-city-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="property_state">State *</Label>
                  <Input
                    id="property_state"
                    name="property_state"
                    value={formData.property_state}
                    onChange={handleChange}
                    placeholder="MI"
                    maxLength={2}
                    required
                    data-testid="property-state-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="property_zip">Zip Code *</Label>
                  <Input
                    id="property_zip"
                    name="property_zip"
                    value={formData.property_zip}
                    onChange={handleChange}
                    placeholder="48201"
                    maxLength={5}
                    pattern="[0-9]{5}"
                    required
                    data-testid="property-zip-input"
                  />
                </div>
              </div>
            </div>

            {/* Financial Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <div className="w-1 h-6 gradient-success rounded-full"></div>
                Financial Details
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="purchase_price">Purchase Price *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                    <Input
                      id="purchase_price"
                      name="purchase_price"
                      type="number"
                      step="0.01"
                      value={formData.purchase_price}
                      onChange={handleChange}
                      placeholder="150000"
                      className="pl-10"
                      required
                      data-testid="purchase-price-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rehab_budget">Rehab Budget *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                    <Input
                      id="rehab_budget"
                      name="rehab_budget"
                      type="number"
                      step="0.01"
                      value={formData.rehab_budget}
                      onChange={handleChange}
                      placeholder="50000"
                      className="pl-10"
                      required
                      data-testid="rehab-budget-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="arv">After Repair Value (ARV) *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                    <Input
                      id="arv"
                      name="arv"
                      type="number"
                      step="0.01"
                      value={formData.arv}
                      onChange={handleChange}
                      placeholder="250000"
                      className="pl-10"
                      required
                      data-testid="arv-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reserve_required_amount">Reserve Required Amount *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                    <Input
                      id="reserve_required_amount"
                      name="reserve_required_amount"
                      type="number"
                      step="0.01"
                      value={formData.reserve_required_amount}
                      onChange={handleChange}
                      placeholder="30000"
                      className="pl-10"
                      required
                      data-testid="reserve-required-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <div className="w-1 h-6 gradient-accent rounded-full"></div>
                Additional Information
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="exit_strategy">Exit Strategy *</Label>
                  <Input
                    id="exit_strategy"
                    name="exit_strategy"
                    value={formData.exit_strategy}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Refinance, Sell"
                    data-testid="exit-strategy-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="est_closing_date">Estimated Closing Date *</Label>
                  <Input
                    id="est_closing_date"
                    name="est_closing_date"
                    type="date"
                    value={formData.est_closing_date}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    data-testid="closing-date-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Any additional information about the deal..."
                  data-testid="notes-textarea"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button type="button" variant="outline" onClick={() => navigate('/borrower')}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="gradient-primary border-0 hover-scale" data-testid="submit-deal-button">
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Creating...' : 'Create Deal'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default BorrowerDealCreate;